import React, { useCallback, useEffect, useLayoutEffect, useState } from 'react'
import Button from 'react-bootstrap/Button'
import FormGroup from 'react-bootstrap/FormGroup'
import FormLabel from 'react-bootstrap/FormLabel'
import Row from 'react-bootstrap/Row'
import styled from 'styled-components'
import DataGrid from 'react-data-grid'
import { useNavigate } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import { getUsers, registerUser, resetUserPassword } from 'app/services/client'
import {
    makeTokenHeaderConfig,
    useTokenHeaderConfig,
} from 'app/services/tokenConfig'
import { isTokenExpired } from 'app/utils/hooks'
import { APP_ROUTES } from 'app/components/AppRouter/routes'
import { columnsConfig } from './columnsConfig'
import { User } from 'domain/User'
import { NewUserModal } from './NewUserModal'
import { handleServerAPIError } from 'app/services/utils'
import NoGridData from 'app/components/NoGridData'

const StyledTitle = styled.h1`
    margin-left: -15px;
`

const StyledButton = styled(Button)`
    width: 160px;
    margin-bottom: 10px;
`

const FreshUserContainer = styled.div`
    background-color: whitesmoke;
    border: 1px solid #495057;
    border-radius: 5px;
    margin-bottom: 10px;
    padding-top: 10px;
`

const UserView = () => {
    const [isVisible, setIsVisible] = useState<boolean>(false)
    const [users, setUsers] = useState<User[]>([])
    const [freshUser, setFreshUser] = useState<User>()
    const navigate = useNavigate()
    const dispatch = useDispatch()
    const [token] = useTokenHeaderConfig()

    useLayoutEffect(() => {
        if (token) {
            getUsers(makeTokenHeaderConfig(token))
                .then(res => {
                    setUsers(
                        res.data.value.map((user, i) => ({
                            ...user,
                            order: i + 1,
                        })),
                    )
                })
                .catch(e => handleServerAPIError(e, dispatch, navigate))
        }
    }, [token])

    useEffect(() => {
        if (isTokenExpired()) navigate(APP_ROUTES.LOGIN)
    }, [])

    const createUser = useCallback(
        (login: string, level: number) => {
            registerUser(login, level, makeTokenHeaderConfig(token))
                .then(res => {
                    setFreshUser(res.data)

                    getUsers(makeTokenHeaderConfig(token)).then(usersRes => {
                        setUsers(
                            usersRes.data.value.map((user, i) => ({
                                ...user,
                                order: i + 1,
                            })),
                        )
                    })
                })
                .catch(e => handleServerAPIError(e, dispatch, navigate))
        },
        [
            registerUser,
            token,
            makeTokenHeaderConfig,
            setFreshUser,
            getUsers,
            makeTokenHeaderConfig,
            token,
            setUsers,
            handleServerAPIError,
            dispatch,
            navigate,
        ],
    )

    const resetUserPasswordCallback = useCallback(
        (login: string) => {
            resetUserPassword(login, makeTokenHeaderConfig(token))
                .then(res => {
                    setFreshUser(res.data)
                })
                .catch(e => handleServerAPIError(e, dispatch, navigate))
        },
        [
            resetUserPassword,
            makeTokenHeaderConfig,
            token,
            setFreshUser,
            handleServerAPIError,
            dispatch,
            navigate,
        ],
    )

    return (
        <>
            <NewUserModal
                isVisible={isVisible}
                toggle={() => setIsVisible(false)}
                create={createUser}
            />
            <Row>
                <StyledTitle>Users Management</StyledTitle>
            </Row>
            <Row>
                <StyledButton
                    variant="danger"
                    onClick={() => setIsVisible(true)}>
                    Register new user
                </StyledButton>
            </Row>
            {freshUser && (
                <Row>
                    <FreshUserContainer>
                        <FormGroup className="mb-3">
                            <FormLabel>
                                <b>Temporary credentials of the user</b>
                            </FormLabel>
                        </FormGroup>
                        <FormGroup className="mb-3">
                            <FormLabel>
                                <b>ID:</b>
                            </FormLabel>
                            <FormLabel style={{ marginLeft: '10px' }}>
                                {freshUser?.id}
                            </FormLabel>
                        </FormGroup>
                        <FormGroup className="mb-3">
                            <FormLabel>
                                <b>Login:</b>
                            </FormLabel>
                            <FormLabel style={{ marginLeft: '10px' }}>
                                {freshUser?.login}
                            </FormLabel>
                        </FormGroup>
                        <FormGroup className="mb-3">
                            <FormLabel>
                                <b>Password:</b>
                            </FormLabel>
                            <FormLabel style={{ marginLeft: '10px' }}>
                                {freshUser?.password}
                            </FormLabel>
                        </FormGroup>
                        <FormGroup className="mb-3">
                            <FormLabel>
                                <b>Level:</b>
                            </FormLabel>
                            <FormLabel style={{ marginLeft: '10px' }}>
                                {freshUser?.level}
                            </FormLabel>
                        </FormGroup>
                    </FreshUserContainer>
                </Row>
            )}
            <Row>
                <DataGrid
                    columns={columnsConfig({
                        resetPassword: resetUserPasswordCallback,
                    })}
                    rows={users}
                    rowHeight={45}
                    renderers={{ noRowsFallback: <NoGridData /> }}
                    style={{ height: '100%', minHeight: '500px' }}
                />
            </Row>
        </>
    )
}

export default UserView
